import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchLocation } from '../utils/nativeApi';
import { clearGenerationState } from '../services/domainTtsService';
import { trackEvent, trackPageView } from '../services/analytics';

const SmartNotebookInput = () => {
  // Debug log for Xcode console
  console.log('📝 [SmartNotebookInput] Component MOUNTING...');
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isLandingSource = searchParams.get('source') === 'landing' || searchParams.get('source') === 'watch';
  const isEmergentSource = searchParams.get('source') === 'emergent';
  const sourceParam = isLandingSource ? 'landing' : (isEmergentSource ? 'emergent' : '');
  const [input, setInput] = useState('');
  
  // Restore saved form state on mount — try sessionStorage first (SPA-safe), then localStorage
  const savedFormState = (() => {
    try {
      const session = sessionStorage.getItem('onboarding_form_draft');
      if (session) return JSON.parse(session);
      const local = localStorage.getItem('onboarding_form_draft');
      if (local) return JSON.parse(local);
      return null;
    } catch { return null; }
  })();
  
  const [parsedData, setParsedData] = useState({
    name: savedFormState?.name || '',
    dateOfBirth: savedFormState?.dateOfBirth || '',
    timeOfBirth: savedFormState?.timeOfBirth || '',
    placeOfBirth: savedFormState?.placeOfBirth || '',
    gender: savedFormState?.gender || ''
  });
  const [suggestions, setSuggestions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showGenderPicker, setShowGenderPicker] = useState(false);
  const [timeAmPm, setTimeAmPm] = useState(savedFormState?.timeAmPm || 'AM');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [tempDay, setTempDay] = useState(savedFormState?.tempDay || '');
  const [tempMonth, setTempMonth] = useState(savedFormState?.tempMonth || '');
  const [tempYear, setTempYear] = useState(savedFormState?.tempYear || '');
  const [datePickerMode, setDatePickerMode] = useState('day'); // 'day', 'month', 'year'
  const [activeField, setActiveField] = useState(null); // Track which field is currently active
  const [locationSelected, setLocationSelected] = useState(savedFormState?.locationSelected || false); // Track if location was selected from dropdown
  const inputRef = useRef(null);
  const datePickerRef = useRef(null);
  const locationDropdownRef = useRef(null);

  // Debug: Log when component is fully initialized
  useEffect(() => {
    console.log('📝 [SmartNotebookInput] Component MOUNTED, isMobile:', isMobile);
    console.log('📝 [SmartNotebookInput] Window size:', window.innerWidth, 'x', window.innerHeight);
    console.log('📝 [SmartNotebookInput] Location:', location.pathname);
    trackPageView('onboarding');
    trackEvent('onboarding_start', { source: isLandingSource ? 'landing' : 'app' });
    
    return () => {
      console.log('📝 [SmartNotebookInput] Component UNMOUNTING');
    };
  }, []);

  // NO auto-clear - let user data persist between pages
  // Data is only cleared when user explicitly generates a NEW story

  // Click outside handler to close pickers/dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close date picker if clicking outside
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        // Check if click is not on date input fields
        const isDateInput = event.target.id === 'day-input' || 
                           event.target.id === 'month-input' || 
                           event.target.id === 'year-input';
        if (!isDateInput) {
          setShowDatePicker(false);
        }
      }
      
      // Close location dropdown if clicking outside
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        const isLocationInput = event.target.placeholder === 'e.g., New York';
        if (!isLocationInput) {
          setLocationSuggestions([]);
        }
      }
      
      // Close gender picker if clicking outside
      if (showGenderPicker) {
        const isGenderButton = event.target.closest('[data-gender-picker]');
        if (!isGenderButton) {
          setShowGenderPicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker, showGenderPicker]);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Mark that user is in onboarding (for app resume)
    localStorage.setItem('onboarding_in_progress', 'true');
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-save form state (survives app kill via localStorage, survives SPA nav via sessionStorage)
  useEffect(() => {
    const draft = {
      name: parsedData.name,
      dateOfBirth: parsedData.dateOfBirth,
      timeOfBirth: parsedData.timeOfBirth,
      placeOfBirth: parsedData.placeOfBirth,
      gender: parsedData.gender,
      timeAmPm,
      tempDay,
      tempMonth,
      tempYear,
      locationSelected
    };
    // Guard: Don't overwrite a valid saved draft with all-empty values
    const hasAnyData = parsedData.name || parsedData.dateOfBirth || parsedData.timeOfBirth || parsedData.placeOfBirth || parsedData.gender;
    if (!hasAnyData) {
      try {
        const existing = localStorage.getItem('onboarding_form_draft');
        if (existing) {
          const saved = JSON.parse(existing);
          if (saved.name || saved.dateOfBirth || saved.timeOfBirth) return; // Don't overwrite valid draft with empty
        }
      } catch {}
    }
    const json = JSON.stringify(draft);
    try { sessionStorage.setItem('onboarding_form_draft', json); } catch {}
    try { localStorage.setItem('onboarding_form_draft', json); } catch {}
  }, [parsedData, timeAmPm, tempDay, tempMonth, tempYear, locationSelected]);

  // Sync temp date values with parsedData when it changes
  useEffect(() => {
    if (parsedData.dateOfBirth) {
      const parts = parsedData.dateOfBirth.split('-');
      if (parts.length === 3) {
        setTempDay(parts[0] || '');
        setTempMonth(parts[1] || '');
        setTempYear(parts[2] || '');
      }
    }
  }, [parsedData.dateOfBirth]);

  // Add shine animation CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shine {
        0% { left: -100%; }
        50% { left: 100%; }
        100% { left: 100%; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Advanced parser that handles ANY sequence and spacing
  const parseInput = (text) => {
    const parsed = {
      name: '',
      dateOfBirth: '',
      timeOfBirth: '',
      placeOfBirth: '',
      gender: ''
    };

    // Month name to number mapping
    const monthMap = {
      'jan': '01', 'january': '01',
      'feb': '02', 'february': '02',
      'mar': '03', 'march': '03',
      'apr': '04', 'april': '04',
      'may': '05',
      'jun': '06', 'june': '06',
      'jul': '07', 'july': '07',
      'aug': '08', 'august': '08',
      'sep': '09', 'sept': '09', 'september': '09',
      'oct': '10', 'october': '10',
      'nov': '11', 'november': '11',
      'dec': '12', 'december': '12'
    };

    // Normalize text: add spaces around special characters for easier parsing
    let normalized = text.replace(/([,;])/g, ' $1 ');
    
    // Extract gender first (so we can exclude it from other matches)
    const genderMatch = normalized.match(/\b(male|female|m|f|man|woman|boy|girl)\b/i);
    if (genderMatch) {
      const g = genderMatch[1].toLowerCase();
      if (g === 'm' || g === 'male' || g === 'man' || g === 'boy') {
        parsed.gender = 'Male';
      } else if (g === 'f' || g === 'female' || g === 'woman' || g === 'girl') {
        parsed.gender = 'Female';
      }
      // Remove gender from text for cleaner parsing
      normalized = normalized.replace(genderMatch[0], '');
    }

    // Extract date and convert to DD-MM-YYYY format
    // Pattern 1: DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    let dateMatch = normalized.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
    if (dateMatch) {
      const day = dateMatch[1].padStart(2, '0');
      const month = dateMatch[2].padStart(2, '0');
      const year = dateMatch[3];
      parsed.dateOfBirth = `${day}-${month}-${year}`;
      normalized = normalized.replace(dateMatch[0], ' ');
    }
    
    // Pattern 2: YYYY/MM/DD or YYYY-MM-DD
    if (!parsed.dateOfBirth) {
      dateMatch = normalized.match(/(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/);
      if (dateMatch) {
        const year = dateMatch[1];
        const month = dateMatch[2].padStart(2, '0');
        const day = dateMatch[3].padStart(2, '0');
        parsed.dateOfBirth = `${day}-${month}-${year}`;
        normalized = normalized.replace(dateMatch[0], ' ');
      }
    }
    
    // Pattern 3: "16 May 1990" or "May 16 1990" (month name with spaces)
    if (!parsed.dateOfBirth) {
      dateMatch = normalized.match(/(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{4})/i);
      if (dateMatch) {
        const day = dateMatch[1].padStart(2, '0');
        const monthName = dateMatch[2].toLowerCase();
        const month = monthMap[monthName] || '01';
        const year = dateMatch[3];
        parsed.dateOfBirth = `${day}-${month}-${year}`;
        normalized = normalized.replace(dateMatch[0], ' ');
      }
    }
    
    // Pattern 4: "May 16 1990"
    if (!parsed.dateOfBirth) {
      dateMatch = normalized.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{1,2})\s*[,]?\s*(\d{4})/i);
      if (dateMatch) {
        const monthName = dateMatch[1].toLowerCase();
        const month = monthMap[monthName] || '01';
        const day = dateMatch[2].padStart(2, '0');
        const year = dateMatch[3];
        parsed.dateOfBirth = `${day}-${month}-${year}`;
        normalized = normalized.replace(dateMatch[0], ' ');
      }
    }

    // Extract time and convert to HH:MM AM/PM format
    // Pattern 1: "4:15 PM" or "4:15PM"
    let timeMatch = normalized.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
    if (timeMatch) {
      let hour = parseInt(timeMatch[1]);
      let minute = parseInt(timeMatch[2]);
      const ampm = timeMatch[3].toUpperCase();
      // Clamp to valid ranges
      if (hour > 12) hour = 12;
      if (hour < 1) hour = 1;
      if (minute > 59) minute = 59;
      if (minute < 0) minute = 0;
      parsed.timeOfBirth = `${hour}:${String(minute).padStart(2, '0')} ${ampm}`;
      normalized = normalized.replace(timeMatch[0], ' ');
    }
    
    // Pattern 2: "4PM" or "4 PM" (hour only)
    if (!parsed.timeOfBirth) {
      timeMatch = normalized.match(/(\d{1,2})\s*(am|pm)/i);
      if (timeMatch) {
        const hour = timeMatch[1];
        const ampm = timeMatch[2].toUpperCase();
        parsed.timeOfBirth = `${hour}:00 ${ampm}`;
        normalized = normalized.replace(timeMatch[0], ' ');
      }
    }
    
    // Pattern 3: Military time "16:15"
    if (!parsed.timeOfBirth) {
      timeMatch = normalized.match(/(\d{2}):(\d{2})/);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1]);
        let minute = parseInt(timeMatch[2]);
        if (minute > 59) minute = 59;
        if (minute < 0) minute = 0;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        if (hour > 12) hour -= 12;
        if (hour === 0) hour = 12;
        parsed.timeOfBirth = `${hour}:${String(minute).padStart(2, '0')} ${ampm}`;
        normalized = normalized.replace(timeMatch[0], ' ');
      }
    }

    // Clean up the remaining text
    normalized = normalized.replace(/\s+/g, ' ').trim();
    normalized = normalized.replace(/[,;]+/g, ' ').trim();
    
    // Extract name (first sequence of words that look like names)
    const nameMatch = normalized.match(/^([A-Za-z]+(?:\s+[A-Za-z]+)?)/);
    if (nameMatch) {
      parsed.name = nameMatch[1].trim();
      normalized = normalized.replace(nameMatch[0], '').trim();
    }

    // Extract place (remaining capitalized words)
    const placeWords = normalized.split(/\s+/).filter(word => 
      word && 
      word.length > 1 && 
      word[0] === word[0].toUpperCase() &&
      !/^\d+$/.test(word)
    );
    
    if (placeWords.length > 0) {
      parsed.placeOfBirth = placeWords.join(' ');
    }

    return parsed;
  };

  // Generate smart suggestions based on current input
  const generateSuggestions = (text) => {
    const suggestions = [];
    const parsed = parseInput(text);
    
    // Suggest next field to fill
    if (!parsed.name) {
      suggestions.push('Start with your name...');
    } else if (!parsed.dateOfBirth) {
      suggestions.push('Add your birth date (e.g., Jan 15 1990)');
    } else if (!parsed.timeOfBirth) {
      suggestions.push('Add birth time (e.g., 3:45 PM)');
    } else if (!parsed.placeOfBirth) {
      suggestions.push('Add birth place (e.g., New York)');
    } else if (!parsed.gender) {
      suggestions.push('Add gender (Male/Female)');
    }

    // Example suggestions
    if (text.length === 0) {
      suggestions.push('Example: John Doe, Jan 15 1990, 3:45 PM, New York, Male');
      suggestions.push('Example: Sarah Smith, 05/12/1995, 10:30 AM, London, Female');
    }

    return suggestions;
  };

  // Track if this is first mount with saved state (skip parseInput reset)
  const hasRestoredRef = useRef(!!savedFormState);

  // Safety net: if parsedData is empty after mount but localStorage has valid draft, restore it
  useEffect(() => {
    if (parsedData.name || parsedData.timeOfBirth) return; // Already has data, no need
    try {
      const local = localStorage.getItem('onboarding_form_draft');
      if (!local) return;
      const saved = JSON.parse(local);
      if (saved.name || saved.timeOfBirth || saved.dateOfBirth) {
        console.log('📝 [SmartNotebookInput] Safety net: restoring from localStorage', saved.name);
        setParsedData(prev => ({
          name: saved.name || prev.name || '',
          dateOfBirth: saved.dateOfBirth || prev.dateOfBirth || '',
          timeOfBirth: saved.timeOfBirth || prev.timeOfBirth || '',
          placeOfBirth: saved.placeOfBirth || prev.placeOfBirth || '',
          gender: saved.gender || prev.gender || '',
        }));
        if (saved.timeAmPm) setTimeAmPm(saved.timeAmPm);
        if (saved.tempDay) setTempDay(saved.tempDay);
        if (saved.tempMonth) setTempMonth(saved.tempMonth);
        if (saved.tempYear) setTempYear(saved.tempYear);
        if (saved.locationSelected) setLocationSelected(saved.locationSelected);
        hasRestoredRef.current = true;
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update parsed data and suggestions as user types
  useEffect(() => {
    // On first mount with restored state, don't overwrite with parseInput('') 
    if (hasRestoredRef.current && input === '') {
      hasRestoredRef.current = false;
      setShowPreview(Object.values(parsedData).some(v => v !== ''));
      return;
    }
    hasRestoredRef.current = false;
    
    const parsed = parseInput(input);
    setParsedData(parsed);
    
    // Show preview when we have some data
    const filledCount = Object.values(parsed).filter(v => v !== '').length;
    setShowPreview(filledCount > 0);
    
    // Generate suggestions
    if (input.length > 0) {
      const newSuggestions = generateSuggestions(input);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions(generateSuggestions(''));
    }
  }, [input]);

  // Validation functions
  const validateField = (fieldKey, value) => {
    if (!value) return null; // No validation if empty
    
    switch(fieldKey) {
      case 'name':
        return value.length >= 2 && /^[a-zA-Z\s\.]+$/.test(value);
      
      case 'dateOfBirth':
        const dateParts = value.split('-');
        if (dateParts.length !== 3) return false;
        const day = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]);
        const year = parseInt(dateParts[2]);
        return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1800 && year <= 2025;
      
      case 'timeOfBirth':
        const timeMatch = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!timeMatch) return false;
        const hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        return hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59;
      
      case 'placeOfBirth':
        // Only valid if location was selected from dropdown
        return locationSelected && value.length >= 3 && value.length <= 200;
      
      case 'gender':
        return ['Male', 'Female'].includes(value);
      
      default:
        return true;
    }
  };

  // Handle direct field changes (works for both mobile and desktop)
  const handleFieldChange = (fieldKey, value) => {
    setParsedData(prev => ({ ...prev, [fieldKey]: value }));
  };

  // Recalculate completion based on VALID fields whenever data or locationSelected changes
  useEffect(() => {
    const fieldKeys = ['name', 'dateOfBirth', 'timeOfBirth', 'gender', 'placeOfBirth'];
    const validCount = fieldKeys.filter(k => {
      let v = parsedData[k];
      // For dateOfBirth, construct from temp state (same source as the ✓/✗ icon)
      if (k === 'dateOfBirth' && (!v || v === '') && tempDay && tempMonth && tempYear?.length === 4) {
        v = `${tempDay}-${tempMonth}-${tempYear}`;
      }
      if (!v || v === '') return false;
      return validateField(k, v) === true;
    }).length;
    setCompletionPercentage((validCount / 5) * 100);
  }, [parsedData, locationSelected, tempDay, tempMonth, tempYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync parsedData.dateOfBirth from temp date parts
  useEffect(() => {
    if (tempDay && tempMonth && tempYear?.length === 4) {
      const composed = `${tempDay.padStart(2, '0')}-${tempMonth.padStart(2, '0')}-${tempYear}`;
      if (parsedData.dateOfBirth !== composed) {
        setParsedData(prev => ({ ...prev, dateOfBirth: composed }));
      }
    }
  }, [tempDay, tempMonth, tempYear]); // eslint-disable-line react-hooks/exhaustive-deps


  // Debounce timer ref for location search
  const locationSearchTimeout = useRef(null);

  // Enhanced location autocomplete with fast debouncing - Native compatible
  const handleLocationSearch = async (query) => {
    // Clear previous timeout
    if (locationSearchTimeout.current) {
      clearTimeout(locationSearchTimeout.current);
    }

    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    // Shorter debounce: 150ms for faster response
    locationSearchTimeout.current = setTimeout(async () => {
      try {
        console.log('[LocationSearch] Searching for:', query);
        const results = await searchLocation(query);
        console.log('[LocationSearch] Results:', results);
        setLocationSuggestions(results);
      } catch (error) {
        console.error('[LocationSearch] Error:', error);
        setLocationSuggestions([]);
      }
    }, 150);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleSubmit = async () => {
    // Silently block submit if fields aren't valid (progress bar shows status)
    const fieldKeys = ['name', 'dateOfBirth', 'timeOfBirth', 'gender', 'placeOfBirth'];
    const allValid = fieldKeys.every(k => {
      const v = parsedData[k];
      return v && v !== '' && validateField(k, v) === true;
    });
    
    if (!allValid) return;

    trackEvent('onboarding_submit', { source: isLandingSource ? 'landing' : 'app', name: parsedData.name });

    // Time format already validated above via validateField
    setIsSubmitting(true);
    setValidationErrors({});

    try {
      // Merge coordinates from birthCoordinates localStorage
      let coordinates = { lat: 28.6139, lon: 77.2090 }; // Default to New Delhi
      try {
        const storedCoords = localStorage.getItem('birthCoordinates');
        if (storedCoords) {
          coordinates = JSON.parse(storedCoords);
        }
      } catch (e) { /* use defaults */ }

      // Generate session ID for MongoDB persistence
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Parse dateOfBirth (DD-MM-YYYY) into year, month, day
      let year, month, day;
      if (parsedData.dateOfBirth) {
        const [d, m, y] = parsedData.dateOfBirth.split('-').map(Number);
        day = d; month = m; year = y;
      }
      
      // Parse timeOfBirth into hour, minute (24-hour format)
      let hour = 12, minute = 0;
      if (parsedData.timeOfBirth) {
        const timeParts = parsedData.timeOfBirth.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeParts) {
          hour = parseInt(timeParts[1]);
          minute = parseInt(timeParts[2]);
          if (timeParts[3]?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
          if (timeParts[3]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
        }
      }

      // Create complete birth data with coordinates AND parsed date/time
      const completeBirthData = {
        ...parsedData,
        // Parsed date/time fields for API calls
        year,
        month,
        day,
        hour,
        minute,
        // Coordinates
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        place: parsedData.placeOfBirth,
        session_id: sessionId,
        // Device ID for linking to user after login
        device_id: localStorage.getItem('taraanaya_device_id') || `device_${Date.now()}`,
        // User ID if already logged in
        user_id: localStorage.getItem('user_id') || null
      };

      // Use Railway production URL as fallback for native iOS/Android
      const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
      const backendUrl = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;
      
      // Ensure device_id exists in localStorage
      if (!localStorage.getItem('taraanaya_device_id')) {
        localStorage.setItem('taraanaya_device_id', completeBirthData.device_id);
      }
      
      // IMPORTANT: Clear ALL previous user's cached data before saving new user
      // This ensures new user doesn't see previous user's data - COMPREHENSIVE CLEAR
      const domains = ['health', 'career', 'wealth', 'marriage', 'education', 'spiritual', 'travel', 'family'];
      domains.forEach(d => localStorage.removeItem(`${d}_timeline_data`));
      
      // Clear ALL user-specific cached data
      localStorage.removeItem('domain_insights_cache');
      localStorage.removeItem('timeline_data_cache');
      localStorage.removeItem('unified_story_data');
      localStorage.removeItem('story_generated');
      localStorage.removeItem('watching_story');
      localStorage.removeItem('audio_user_id');
      localStorage.removeItem('session_id');
      localStorage.removeItem('birth_session_id');
      localStorage.removeItem('user_id');
      localStorage.removeItem('prediction_id');
      localStorage.removeItem('birthDetails');
      localStorage.removeItem('birth_details');
      localStorage.removeItem('birthCoordinates');
      localStorage.removeItem('past_story_cache');
      localStorage.removeItem('future_story_cache');
      localStorage.removeItem('hex_story_cache');
      localStorage.removeItem('dashboard_cache');
      localStorage.removeItem('daily_prediction_cache');
      localStorage.removeItem('taraanaya_user_profile');
      
      // Clear TTS generation state to prevent loading previous user's audio
      clearGenerationState();
      
      // Generate NEW unique device ID for this fresh user session BEFORE saving birth details
      // This ensures birth details and all subsequent operations use the same device_id
      const newDeviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('taraanaya_device_id', newDeviceId);
      localStorage.setItem('device_id', newDeviceId);
      
      // Update completeBirthData with the new device_id
      completeBirthData.device_id = newDeviceId;
      
      console.log('🗑️ Cleared ALL previous user cache for new onboarding - New device ID:', newDeviceId);
      
      // STEP 1: Save to localStorage IMMEDIATELY (guaranteed to work on iOS)
      localStorage.setItem('birthDetails', JSON.stringify(completeBirthData));
      localStorage.setItem('prediction_id', 'demo_' + Date.now());
      console.log('✅ Birth details saved to localStorage:', completeBirthData);
      
      // Clear onboarding flag and form draft - user completed this step
      localStorage.removeItem('onboarding_in_progress');
      localStorage.removeItem('onboarding_form_draft');
      sessionStorage.removeItem('onboarding_form_draft');
      // Mark story generation in progress (for app resume)
      localStorage.setItem('story_generation_in_progress', 'true');
      sessionStorage.setItem('story_generation_in_progress', 'true');
      
      // STEP 2: Navigate IMMEDIATELY (don't wait for MongoDB)
      navigate(sourceParam ? `/generating-story?source=${sourceParam}` : '/generating-story');
      
      // STEP 3: Try to save to MongoDB in background (non-blocking)
      try {
        fetch(`${backendUrl}/api/birth-details/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(completeBirthData)
        }).then(response => response.json())
          .then(result => {
            if (result.success) {
              console.log('✅ Birth details also saved to MongoDB:', result.session_id);
              localStorage.setItem('birth_session_id', result.session_id);
            }
          })
          .catch(err => console.log('⚠️ MongoDB save failed (non-critical):', err.message));
      } catch (e) {
        // Ignore MongoDB errors - localStorage is enough
      }
      
    } catch (error) {
      console.error('Save error:', error);
      // Final fallback: still try to save and navigate
      let coordinates = { lat: 28.6139, lon: 77.2090 };
      try {
        const storedCoords = localStorage.getItem('birthCoordinates');
        if (storedCoords) coordinates = JSON.parse(storedCoords);
      } catch (e) { /* use defaults */ }
      
      // Parse date/time for fallback too
      let year, month, day, hour = 12, minute = 0;
      if (parsedData.dateOfBirth) {
        const [d, m, y] = parsedData.dateOfBirth.split('-').map(Number);
        day = d; month = m; year = y;
      }
      if (parsedData.timeOfBirth) {
        const timeParts = parsedData.timeOfBirth.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (timeParts) {
          hour = parseInt(timeParts[1]);
          minute = parseInt(timeParts[2]);
          if (timeParts[3]?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
          if (timeParts[3]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
        }
      }
      
      const completeBirthData = {
        ...parsedData,
        year, month, day, hour, minute,
        latitude: coordinates.lat,
        longitude: coordinates.lon,
        place: parsedData.placeOfBirth
      };
      localStorage.setItem('birthDetails', JSON.stringify(completeBirthData));
      localStorage.setItem('prediction_id', 'demo_' + Date.now());
      console.log('✅ Birth details saved (final fallback):', completeBirthData);
      
      // Clear onboarding flag and form draft - user completed this step
      localStorage.removeItem('onboarding_in_progress');
      localStorage.removeItem('onboarding_form_draft');
      sessionStorage.removeItem('onboarding_form_draft');
      localStorage.setItem('story_generation_in_progress', 'true');
      sessionStorage.setItem('story_generation_in_progress', 'true');
      
      navigate(sourceParam ? `/generating-story?source=${sourceParam}` : '/generating-story');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldIcon = (field) => {
    const icons = {
      name: '👤',
      dateOfBirth: '📅',
      timeOfBirth: '🕐',
      placeOfBirth: '📍',
      gender: '⚧'
    };
    return icons[field] || '';
  };

  return (
    <div 
      data-testid="onboarding-screen"
      style={{ 
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        position: isMobile ? 'fixed' : 'relative',
        inset: isMobile ? 0 : undefined,
        overflow: isMobile ? 'hidden' : 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'none',
        height: isMobile ? '100dvh' : 'auto',
        minHeight: '100dvh',
      }}
    >
      {/* Animated cosmic nebula - matching splash */}
      <motion.div
        style={{
          position: 'absolute',
          width: isMobile ? '400px' : '700px',
          height: isMobile ? '400px' : '700px',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.1) 0%, rgba(138, 43, 226, 0.06) 50%, transparent 80%)',
          filter: 'blur(80px)',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 0
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.7, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Cosmic Background - Scattered Stars (no repeating pattern) */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', opacity: 0.5, zIndex: 0 }}>
        {/* White stars */}
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '5%', top: '10%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '15%', top: '5%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '28%', top: '18%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '42%', top: '8%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '65%', top: '15%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '82%', top: '6%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '92%', top: '22%', boxShadow: '0 0 3px white' }} />
        {/* Colored stars */}
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: '#92FFF7', left: '8%', top: '45%', boxShadow: '0 0 5px #92FFF7' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: '#74B3FF', left: '35%', top: '35%', boxShadow: '0 0 5px #74B3FF' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: '#5F42C9', left: '58%', top: '52%', boxShadow: '0 0 4px #5F42C9' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: '#74B3FF', left: '78%', top: '42%', boxShadow: '0 0 5px #74B3FF' }} />
        {/* More white stars lower */}
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '12%', top: '72%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '25%', top: '85%', boxShadow: '0 0 4px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '48%', top: '78%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '1.5px', height: '1.5px', borderRadius: '50%', background: 'white', left: '68%', top: '88%', boxShadow: '0 0 3px white' }} />
        <div style={{ position: 'absolute', width: '1px', height: '1px', borderRadius: '50%', background: 'white', left: '88%', top: '75%', boxShadow: '0 0 2px white' }} />
        <div style={{ position: 'absolute', width: '2px', height: '2px', borderRadius: '50%', background: 'white', left: '95%', top: '92%', boxShadow: '0 0 4px white' }} />
      </div>
      
      {/* Planet 1 - Top Left - Deep Purple (elegant) */}
      <div style={{
        position: 'absolute',
        top: isMobile ? '8%' : '10%',
        left: isMobile ? '-8%' : '3%',
        width: isMobile ? '80px' : '110px',
        height: isMobile ? '80px' : '110px',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 35%, #6B5FD1, #4A3A8C, #2D2250)',
        opacity: 0.5,
        boxShadow: '0 0 50px rgba(107, 95, 209, 0.4), inset -10px -10px 20px rgba(0, 0, 0, 0.3)',
        animation: 'float 8s ease-in-out infinite',
        zIndex: 0
      }} />
      
      {/* Planet 2 - Top Right - Sky Blue (elegant) */}
      <div style={{
        position: 'absolute',
        top: isMobile ? '5%' : '8%',
        right: isMobile ? '-10%' : '5%',
        width: isMobile ? '70px' : '95px',
        height: isMobile ? '70px' : '95px',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 38%, #89C9FF, #5A8FD6, #3A5F8F)',
        opacity: 0.5,
        boxShadow: '0 0 45px rgba(137, 201, 255, 0.35), inset -8px -8px 15px rgba(0, 0, 0, 0.3)',
        animation: 'float 10s ease-in-out infinite reverse',
        zIndex: 0
      }} />
      
      {/* Planet 3 - Bottom Right - Teal/Cyan (elegant with subtle glow) */}
      <div style={{
        position: 'absolute',
        bottom: isMobile ? '8%' : '12%',
        right: isMobile ? '-12%' : '6%',
        width: isMobile ? '75px' : '100px',
        height: isMobile ? '75px' : '100px',
        borderRadius: '50%',
        background: 'radial-gradient(circle at 38% 38%, #7FE5D8, #5AB3C9, #3A7A8F)',
        opacity: 0.45,
        boxShadow: '0 0 50px rgba(127, 229, 216, 0.3), inset -10px -10px 20px rgba(0, 0, 0, 0.35)',
        animation: 'float 12s ease-in-out infinite',
        zIndex: 0
      }} />
      
      {/* Constellation - Top Right */}
      {!isMobile && (
        <svg style={{
          position: 'absolute',
          top: '5%',
          right: '3%',
          width: '150px',
          height: '150px',
          opacity: 0.15,
          zIndex: 0
        }} viewBox="0 0 150 150">
          <line x1="30" y1="20" x2="60" y2="50" stroke="#92FFF7" strokeWidth="1" />
          <line x1="60" y1="50" x2="90" y2="45" stroke="#74B3FF" strokeWidth="1" />
          <line x1="90" y1="45" x2="110" y2="75" stroke="#5F42C9" strokeWidth="1" />
          <line x1="60" y1="50" x2="75" y2="90" stroke="#74B3FF" strokeWidth="1" />
          <circle cx="30" cy="20" r="2" fill="#92FFF7" />
          <circle cx="60" cy="50" r="2" fill="#74B3FF" />
          <circle cx="90" cy="45" r="2" fill="#5F42C9" />
          <circle cx="110" cy="75" r="2" fill="#74B3FF" />
          <circle cx="75" cy="90" r="2" fill="#92FFF7" />
        </svg>
      )}
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes twinkle {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}} />
    
      <div style={{ 
        maxWidth: isMobile ? '92vw' : '460px',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        margin: '0 auto',
        padding: isMobile ? '0 14px' : '0 24px',
        paddingTop: isMobile ? 'max(12px, env(safe-area-inset-top, 12px))' : '32px',
        paddingBottom: isMobile ? 'max(14px, env(safe-area-inset-bottom, 14px))' : '48px',
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '100%' : 'auto',
        minHeight: isMobile ? undefined : '100dvh',
        maxHeight: isMobile ? '100dvh' : 'none',
        justifyContent: 'center',
        gap: isMobile ? '8px' : '12px'
      }}>
        {/* Header */}
        <motion.div
          className="text-center"
          style={{ marginBottom: '0' }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: isMobile ? 'clamp(17px, 5.4vw, 24px)' : '34px',
              color: '#F3E5AB',
              fontWeight: 300,
              letterSpacing: '0.22em',
              marginBottom: '2px',
              lineHeight: 1.15,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            Begin Your Journey
          </h1>
          <p style={{ 
            color: 'rgba(243,229,171,0.65)', 
            fontSize: isMobile ? 'clamp(9px, 2.5vw, 11px)' : '13px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            fontFamily: "'Cormorant Garamond', serif",
            textTransform: 'uppercase',
          }}>
            Share your cosmic details
          </p>
          
          {/* Important Note — shown inline on mobile */}
          <div style={{
            marginTop: isMobile ? '6px' : '12px',
            padding: isMobile ? '4px 10px' : '8px 14px',
            background: 'rgba(243,229,171,0.06)',
            borderRadius: '999px',
            border: '1px solid rgba(243,229,171,0.18)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              color: 'rgba(243,229,171,0.7)',
              fontSize: isMobile ? '9px' : '11px',
              fontWeight: 500,
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              lineHeight: 1.3,
            }}>
              Birth details can't be changed later
            </span>
          </div>
        </motion.div>

        {/* Notebook with Integrated Slots */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ overflow: 'visible' }}
        >
          <div
            style={{
              background: 'linear-gradient(145deg, rgba(26,35,50,0.55) 0%, rgba(10,14,23,0.65) 100%)',
              borderRadius: isMobile ? '16px' : '22px',
              padding: isMobile ? 'clamp(8px, 1.8vh, 14px) clamp(12px, 3vw, 16px)' : '22px 28px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(243,229,171,0.08)',
              position: 'relative',
              border: '1px solid rgba(243,229,171,0.14)',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              overflow: 'visible'
            }}
          >
            {/* Premium Notebook Slots */}
            <div style={{ position: 'relative', zIndex: 1, paddingLeft: isMobile ? '8px' : '0' }}>
              {[
                { key: 'name', label: 'FULL NAME', hint: 'e.g., John Doe' },
                { key: 'dateOfBirth', label: 'DATE OF BIRTH', hint: 'e.g., Jan 15, 1990' },
                { key: 'timeOfBirth', label: 'TIME OF BIRTH', hint: 'e.g., 3:45 PM' },
                { key: 'gender', label: 'GENDER', hint: 'Male or Female' },
                { key: 'placeOfBirth', label: 'PLACE OF BIRTH', hint: 'e.g., New York' }
              ].map((field, idx) => (
                <div
                  key={field.key}
                  style={{
                    marginBottom: isMobile ? 'clamp(6px, 1.3vh, 12px)' : '20px',
                    position: 'relative'
                  }}
                >
                  {/* Modern Label - Blue when active or incomplete */}
                  <div style={{
                    marginBottom: isMobile ? '3px' : '8px',
                    fontSize: isMobile ? '10px' : '12px',
                    color: activeField === field.key ? '#F3E5AB' : (parsedData[field.key] ? '#22c55e' : 'rgba(243,229,171,0.55)'),
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    fontWeight: activeField === field.key ? 500 : 400,
                    fontFamily: "'Cormorant Garamond', serif",
                    transition: 'color 0.2s ease'
                  }}>
                    {field.label}
                    {activeField === field.key && !parsedData[field.key] && (
                      <span style={{ marginLeft: '6px', fontSize: '8px', color: '#74B3FF' }}>●</span>
                    )}
                  </div>

                  {/* Input line - all fields aligned consistently */}
                  <div style={{ display: 'flex', alignItems: 'center', position: 'relative', width: '100%' }}>
                    {/* Time Input with inline AM/PM bubbles - auto-format without colon */}
                    {field.key === 'timeOfBirth' && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'baseline',
                        width: '100%',
                        position: 'relative',
                        borderBottom: activeField === 'timeOfBirth' ? '2px solid #F3E5AB' : '1.5px solid rgba(243,229,171,0.22)',
                        paddingBottom: isMobile ? '6px' : '10px',
                        transition: 'border-color 0.2s ease'
                      }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={parsedData.timeOfBirth?.replace(/ (AM|PM)/i, '') || ''}
                          placeholder="HH:MM"
                          maxLength={5}
                          onFocus={() => setActiveField('timeOfBirth')}
                          onBlur={() => setActiveField(null)}
                          onChange={(e) => {
                            // Remove non-digits and existing colon
                            let val = e.target.value.replace(/[^\d]/g, '');
                            
                            // Auto-format: insert colon after 2 digits
                            if (val.length > 2) {
                              val = val.slice(0, 2) + ':' + val.slice(2, 4);
                            }
                            
                            // Only update with period if time has value
                            if (val) {
                              handleFieldChange('timeOfBirth', `${val} ${timeAmPm}`);
                            } else {
                              handleFieldChange('timeOfBirth', '');
                            }
                          }}
                          style={{
                            flex: '1',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            fontSize: '16px',
                            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                            fontWeight: parsedData.timeOfBirth ? 500 : 400,
                            color: parsedData.timeOfBirth ? '#F3E5AB' : 'rgba(243,229,171,0.4)',
                            padding: 0,
                            letterSpacing: '0.2px',
                            minWidth: 0,
                            maxWidth: isMobile ? '65px' : '80px'
                          }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          gap: '4px', 
                          flexShrink: 0,
                          alignItems: 'center',
                          marginLeft: '4px'
                        }}>
                          {['AM', 'PM'].map(period => (
                            <button
                              key={period}
                              onClick={() => {
                                setTimeAmPm(period);
                                const timeValue = parsedData.timeOfBirth?.replace(/ (AM|PM)/i, '') || '';
                                // Only add to timeOfBirth if there's actual time value
                                if (timeValue) {
                                  handleFieldChange('timeOfBirth', `${timeValue} ${period}`);
                                }
                              }}
                              style={{
                                padding: isMobile ? '6px 14px' : '6px 14px',
                                minHeight: '36px',
                                background: timeAmPm === period ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' : 'rgba(243,229,171,0.06)',
                                color: timeAmPm === period ? '#0a0e17' : 'rgba(243,229,171,0.7)',
                                border: 'none',
                                borderRadius: '14px',
                                fontSize: isMobile ? '13px' : '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: timeAmPm === period ? '0 2px 8px rgba(116, 179, 255, 0.35)' : 'none',
                                whiteSpace: 'nowrap',
                                lineHeight: 1
                              }}
                            >
                              {period}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Gender Bubbles */}
                    {field.key === 'gender' && (
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        width: '100%',
                        borderBottom: '1.5px solid rgba(243,229,171,0.22)',
                        paddingBottom: isMobile ? '6px' : '10px'
                      }}>
                        {['Male', 'Female'].map(gen => (
                          <button
                            key={gen}
                            onClick={() => handleFieldChange('gender', gen)}
                            style={{
                              padding: '10px 18px',
                              minHeight: '40px',
                              background: parsedData.gender === gen ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' : 'rgba(243,229,171,0.06)',
                              color: parsedData.gender === gen ? '#0a0e17' : 'rgba(243,229,171,0.7)',
                              border: 'none',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: parsedData.gender === gen ? '0 2px 6px rgba(116, 179, 255, 0.3)' : 'none',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {gen}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Date Input - Smooth auto-flow with pickers */}
                    {field.key === 'dateOfBirth' && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        borderBottom: activeField === 'dateOfBirth' ? '2px solid #F3E5AB' : '1.5px solid rgba(243,229,171,0.22)',
                        paddingBottom: isMobile ? '6px' : '10px',
                        gap: '2px',
                        transition: 'border-color 0.2s ease'
                      }}>
                        <input
                          id="day-input"
                          type="text"
                          inputMode="numeric"
                          value={tempDay}
                          placeholder="DD"
                          maxLength={2}
                          onFocus={() => {
                            setActiveField('dateOfBirth');
                            setShowDatePicker(true);
                            setDatePickerMode('day');
                          }}
                          onKeyDown={(e) => {
                            // Backspace on empty moves to name field
                            if (e.key === 'Backspace' && !tempDay) {
                              e.preventDefault();
                              const nameInput = document.querySelector('input[placeholder="e.g., John Doe"]');
                              nameInput?.focus();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setTempDay(val);
                            // Auto-advance to month when 2 digits entered
                            if (val.length === 2) {
                              setShowDatePicker(false);
                              // Small delay then open month picker
                              setTimeout(() => {
                                document.getElementById('month-input')?.focus();
                              }, 50);
                            }
                            // Update full date if all parts exist
                            if (val && tempMonth && tempYear && tempYear.length === 4) {
                              const day = val.padStart(2, '0');
                              handleFieldChange('dateOfBirth', `${day}-${tempMonth}-${tempYear}`);
                            }
                          }}
                          style={{
                            width: '28px',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            fontSize: '16px',
                            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                            fontWeight: tempDay ? 500 : 400,
                            color: tempDay && (parseInt(tempDay) < 1 || parseInt(tempDay) > 31) ? '#EE5A52' : (tempDay ? '#F3E5AB' : 'rgba(243,229,171,0.4)'),
                            textAlign: 'center',
                            padding: 0,
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ color: 'rgba(243,229,171,0.35)', fontWeight: 400, fontSize: '16px' }}>-</span>
                        <input
                          id="month-input"
                          type="text"
                          inputMode="numeric"
                          value={tempMonth}
                          placeholder="MM"
                          maxLength={2}
                          onFocus={() => {
                            // Auto-open month picker when field gets focus
                            setShowDatePicker(true);
                            setDatePickerMode('month');
                          }}
                          onKeyDown={(e) => {
                            // Backspace on empty moves back to day
                            if (e.key === 'Backspace' && !tempMonth) {
                              e.preventDefault();
                              setShowDatePicker(false);
                              setTimeout(() => {
                                document.getElementById('day-input')?.focus();
                              }, 50);
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setTempMonth(val);
                            // Auto-advance to year when 2 digits entered
                            if (val.length === 2) {
                              setShowDatePicker(false);
                              // Small delay then open year picker
                              setTimeout(() => {
                                document.getElementById('year-input')?.focus();
                              }, 50);
                            }
                            // Update full date if all parts exist
                            if (tempDay && val && tempYear && tempYear.length === 4) {
                              const month = val.padStart(2, '0');
                              handleFieldChange('dateOfBirth', `${tempDay.padStart(2, '0')}-${month}-${tempYear}`);
                            }
                          }}
                          style={{
                            width: '28px',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            fontSize: '16px',
                            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                            fontWeight: tempMonth ? 500 : 400,
                            color: tempMonth && (parseInt(tempMonth) < 1 || parseInt(tempMonth) > 12) ? '#EE5A52' : (tempMonth ? '#F3E5AB' : 'rgba(243,229,171,0.4)'),
                            textAlign: 'center',
                            padding: 0,
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ color: 'rgba(243,229,171,0.35)', fontWeight: 400, fontSize: '16px' }}>-</span>
                        <input
                          id="year-input"
                          type="text"
                          inputMode="numeric"
                          value={tempYear}
                          placeholder="YYYY"
                          maxLength={4}
                          onFocus={() => {
                            // Auto-open year picker when field gets focus
                            setShowDatePicker(true);
                            setDatePickerMode('year');
                          }}
                          onKeyDown={(e) => {
                            // Backspace on empty moves back to month
                            if (e.key === 'Backspace' && !tempYear) {
                              e.preventDefault();
                              setShowDatePicker(false);
                              setTimeout(() => {
                                document.getElementById('month-input')?.focus();
                              }, 50);
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setTempYear(val);
                            // Close picker and move to time when year is complete
                            if (val.length === 4) {
                              setShowDatePicker(false);
                              // Move to time input
                              setTimeout(() => {
                                document.querySelector('input[placeholder="HH:MM"]')?.focus();
                              }, 50);
                            }
                            // Update full date if all parts exist
                            if (tempDay && tempMonth && val && val.length === 4) {
                              handleFieldChange('dateOfBirth', `${tempDay.padStart(2, '0')}-${tempMonth.padStart(2, '0')}-${val}`);
                            }
                          }}
                          style={{
                            width: '46px',
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            fontSize: '16px',
                            fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                            fontWeight: tempYear ? 500 : 400,
                            color: tempYear && (parseInt(tempYear) < 1800 || parseInt(tempYear) > 2025) ? '#EE5A52' : (tempYear ? '#F3E5AB' : 'rgba(243,229,171,0.4)'),
                            textAlign: 'center',
                            padding: 0,
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Other Input fields (name, place) */}
                    {(field.key !== 'timeOfBirth' && field.key !== 'gender' && field.key !== 'dateOfBirth') && (
                      <input
                        type="text"
                        value={parsedData[field.key]}
                        placeholder={field.hint}
                        onFocus={() => setActiveField(field.key)}
                        onBlur={() => setActiveField(null)}
                        onChange={(e) => {
                          handleFieldChange(field.key, e.target.value);
                          if (field.key === 'placeOfBirth') {
                            // Reset locationSelected when user types manually
                            setLocationSelected(false);
                            handleLocationSearch(e.target.value);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Tab or Enter from name field moves to date picker
                          if (field.key === 'name' && (e.key === 'Tab' || e.key === 'Enter')) {
                            e.preventDefault();
                            document.getElementById('day-input')?.focus();
                          }
                        }}
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: activeField === field.key ? '2px solid #F3E5AB' : '1.5px solid rgba(243,229,171,0.22)',
                          outline: 'none',
                          fontSize: '16px',
                          fontFamily: "Aptos, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
                          fontWeight: parsedData[field.key] ? 500 : 400,
                          color: parsedData[field.key] ? '#F3E5AB' : 'rgba(243,229,171,0.4)',
                          paddingBottom: isMobile ? '10px' : '10px',
                          transition: 'border-color 0.2s ease',
                          letterSpacing: '0.2px'
                        }}
                      />
                    )}
                    
                    {/* Date picker with auto-close on selection */}
                    {field.key === 'dateOfBirth' && showDatePicker && (
                      <motion.div
                        ref={datePickerRef}
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '8px',
                          background: 'rgba(15,21,33,0.98)',
                          borderRadius: '12px',
                          padding: '16px',
                          boxShadow: '0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(243,229,171,0.18)',
                          backdropFilter: 'blur(12px)',
                          zIndex: 100
                        }}
                      >
                        {/* Day Picker (1-31) */}
                        {datePickerMode === 'day' && (
                          <>
                            <div style={{ fontSize: '12px', color: 'rgba(243,229,171,0.7)', marginBottom: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              Select Day
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                              {Array.from({ length: 31 }, (_, i) => {
                                const day = String(i + 1).padStart(2, '0');
                                return (
                                  <button
                                    key={day}
                                    onClick={() => {
                                      setTempDay(day);
                                      // Auto-close picker after selection
                                      setShowDatePicker(false);
                                      // Auto-advance: focus month input
                                      setTimeout(() => document.getElementById('month-input')?.focus(), 100);
                                      // Update full date if month and year exist
                                      if (tempMonth && tempYear && tempYear.length === 4) {
                                        handleFieldChange('dateOfBirth', `${day}-${tempMonth}-${tempYear}`);
                                      }
                                    }}
                                    style={{
                                      padding: '8px 4px',
                                      background: tempDay === day ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' : 'rgba(243,229,171,0.05)',
                                      color: tempDay === day ? '#0a0e17' : 'rgba(243,229,171,0.7)',
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                        
                        {/* Month Picker */}
                        {datePickerMode === 'month' && (
                          <>
                            <div style={{ fontSize: '12px', color: 'rgba(243,229,171,0.7)', marginBottom: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              Select Month
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                              {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((month, idx) => {
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                return (
                                  <button
                                    key={month}
                                    onClick={() => {
                                      setTempMonth(month);
                                      // Auto-close picker after selection
                                      setShowDatePicker(false);
                                      // Auto-advance: focus year input
                                      setTimeout(() => document.getElementById('year-input')?.focus(), 100);
                                      // Update full date if day and year exist
                                      if (tempDay && tempYear && tempYear.length === 4) {
                                        handleFieldChange('dateOfBirth', `${tempDay}-${month}-${tempYear}`);
                                      }
                                    }}
                                    style={{
                                      padding: '10px 6px',
                                      background: tempMonth === month ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' : 'rgba(243,229,171,0.05)',
                                      color: tempMonth === month ? '#0a0e17' : 'rgba(243,229,171,0.7)',
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    {monthNames[idx]}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                        
                        {/* Year Picker */}
                        {datePickerMode === 'year' && (
                          <>
                            <div style={{ fontSize: '12px', color: 'rgba(243,229,171,0.7)', marginBottom: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              Select Year
                            </div>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(4, 1fr)', 
                              gap: '6px',
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {Array.from({ length: 226 }, (_, i) => {
                                const year = String(2025 - i);
                                return (
                                  <button
                                    key={year}
                                    onClick={() => {
                                      setTempYear(year);
                                      // Auto-close picker after selection
                                      setShowDatePicker(false);
                                      // Update full date if day and month exist
                                      if (tempDay && tempMonth) {
                                        handleFieldChange('dateOfBirth', `${tempDay}-${tempMonth}-${year}`);
                                      }
                                    }}
                                    style={{
                                      padding: '8px 4px',
                                      background: tempYear === year ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' : 'rgba(243,229,171,0.05)',
                                      color: tempYear === year ? '#0a0e17' : 'rgba(243,229,171,0.7)',
                                      border: 'none',
                                      borderRadius: '8px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    {year}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </motion.div>
                    )}
                    
                    {/* Location Autocomplete - Appears below field, scrolls with form */}
                    {field.key === 'placeOfBirth' && locationSuggestions.length > 0 && (
                      <motion.div
                        ref={locationDropdownRef}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 4px)',
                          left: 0,
                          right: 0,
                          background: 'rgba(15,21,33,0.98)',
                          borderRadius: '8px',
                          boxShadow: '0 12px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(243,229,171,0.18)',
                          backdropFilter: 'blur(12px)',
                          zIndex: 9999,
                          maxHeight: '150px',
                          overflowY: 'auto',
                          border: '1px solid rgba(243,229,171,0.18)'
                        }}
                      >
                        {locationSuggestions.slice(0, 4).map((location, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              const locationName = location.shortDisplay || location.display || location;
                              handleFieldChange('placeOfBirth', locationName);
                              setLocationSelected(true);
                              if (location.lat && location.lon) {
                                localStorage.setItem('birthCoordinates', JSON.stringify({
                                  lat: location.lat,
                                  lon: location.lon
                                }));
                              }
                              setLocationSuggestions([]);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: idx < Math.min(locationSuggestions.length, 4) - 1 ? '1px solid rgba(243,229,171,0.12)' : 'none',
                              fontSize: '13px',
                              color: '#F3E5AB',
                              transition: 'background 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(243,229,171,0.08)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            {location.shortDisplay || location.display || location}
                          </div>
                        ))}
                      </motion.div>
                    )}
                    
                    {/* Validation icon - Tick or Cross */}
                    {(() => {
                      let fieldValue = '';
                      let isValid = null;
                      
                      // Get field value based on field type
                      if (field.key === 'dateOfBirth') {
                        if (tempDay && tempMonth && tempYear && tempYear.length === 4) {
                          fieldValue = `${tempDay}-${tempMonth}-${tempYear}`;
                          isValid = validateField('dateOfBirth', fieldValue);
                        }
                      } else if (field.key === 'gender' || field.key === 'timeOfBirth') {
                        fieldValue = parsedData[field.key];
                        if (fieldValue) {
                          isValid = validateField(field.key, fieldValue);
                        }
                      } else {
                        fieldValue = parsedData[field.key];
                        if (fieldValue) {
                          isValid = validateField(field.key, fieldValue);
                        }
                      }
                      
                      // Show icon only if there's a value
                      if (fieldValue) {
                        return (
                          <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: isValid 
                                ? 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)' 
                                : 'linear-gradient(135deg, #FF6B6B, #EE5A52)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              color: '#fff',
                              fontWeight: 'bold',
                              boxShadow: isValid 
                                ? '0 2px 8px rgba(116, 179, 255, 0.4)' 
                                : '0 2px 8px rgba(255, 107, 107, 0.4)',
                              flexShrink: 0,
                              marginLeft: field.key === 'dateOfBirth' ? '8px' : '0'
                            }}
                          >
                            {isValid ? '✓' : '✕'}
                          </motion.div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              ))}

              {/* Quick Fill Area - Desktop Only */}
              {!isMobile && (
              <div 
                style={{ 
                  marginTop: 'clamp(8px, 1.5vh, 16px)', 
                  paddingTop: 'clamp(8px, 1.5vh, 16px)', 
                  borderTop: '1px solid #e0e0e0'
                }}
              >
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif"
                }}>
                  QUICK FILL
                </div>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type all at once: John Doe Jan 15 1990 3:45 PM New York Male"
                  style={{
                    width: '100%',
                    minHeight: '44px',
                    background: 'rgba(116, 179, 255, 0.03)',
                    border: '1.5px solid #e0e0e0',
                    borderRadius: '8px',
                    outline: 'none',
                    fontSize: '15px',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 400,
                    color: '#F3E5AB',
                    resize: 'none',
                    padding: '14px 16px',
                    lineHeight: '1.6',
                    letterSpacing: '0.2px',
                    transition: 'border-color 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#74B3FF';
                    e.currentTarget.style.background = 'rgba(116, 179, 255, 0.05)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.background = 'rgba(116, 179, 255, 0.03)';
                  }}
                  autoFocus
                />
              </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Premium CTA Button with Progress Fill */}
        <motion.button
          onClick={handleSubmit}
          disabled={completionPercentage < 100}
          data-testid="see-your-story-btn"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          style={{
            width: '100%',
            marginTop: isMobile ? 'clamp(8px, 1.5vh, 14px)' : '20px',
            padding: isMobile ? 'clamp(11px, 2vh, 14px) 16px' : '16px 28px',
            background: '#1a1a2e',
            color: completionPercentage === 100 ? '#0a0e17' : 'rgba(243,229,171,0.4)',
            border: completionPercentage === 100 ? '1px solid rgba(243,229,171,0.5)' : '1px solid rgba(243,229,171,0.15)',
            borderRadius: '999px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            letterSpacing: '0.22em',
            cursor: completionPercentage === 100 ? 'pointer' : 'not-allowed',
            boxShadow: completionPercentage === 100
              ? '0 8px 32px rgba(212,175,55,0.35), inset 0 1px 0 rgba(255,255,255,0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.4s ease',
            fontFamily: "'Cormorant Garamond', serif",
            textTransform: 'uppercase',
            position: 'relative',
            overflow: 'hidden'
          }}
          whileHover={completionPercentage === 100 ? { 
            scale: 1.02,
            boxShadow: '0 12px 40px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.4)'
          } : {}}
          whileTap={completionPercentage === 100 ? { scale: 0.98 } : {}}
        >
          {/* Gold gradient fill — progress from left as user fills fields */}
          <motion.div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)',
              borderRadius: '999px',
              zIndex: 0
            }}
            initial={{ width: '0%' }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
          
          {/* Shine effect for completed button */}
          {completionPercentage === 100 && (
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                zIndex: 1
              }}
              animate={{
                left: ['100%', '100%']
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear'
              }}
            />
          )}
          
          <span style={{ position: 'relative', zIndex: 2 }}>
            See Your Story
          </span>
        </motion.button>

        {/* Legal Links — always sit below CTA, proper breathing room */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(8px, 1.5vh, 16px)', paddingBottom: '4px', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '18px' }}>
            <a href="/privacy-policy" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(10px, 2.8vw, 12px)',
              color: 'rgba(243,229,171,0.55)',
              textDecoration: 'none',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              borderBottom: '1px solid rgba(243,229,171,0.3)',
              paddingBottom: '1px',
            }}>Privacy Policy</a>
            <a href="/terms-of-service" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(10px, 2.8vw, 12px)',
              color: 'rgba(243,229,171,0.55)',
              textDecoration: 'none',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              borderBottom: '1px solid rgba(243,229,171,0.3)',
              paddingBottom: '1px',
            }}>Terms of Service</a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SmartNotebookInput;
